import { Link, useParams } from 'react-router';
import { canEditErrorCodeDocs, customerCodes, type ErrorLane, type ErrorReasonEntry } from '@infras/shared-types';
import { DetailHead } from '../components/DetailHead';
import { DetailBody, RailCard } from '../components/DetailRail';
import { CopyButton } from '../components/CopyButton';
import { ArticleField, useErrorCodeDocs } from '../components/ErrorCodeArticle';
import { useMe } from '../lib/useMe';

/**
 * One customer error CODE.
 *
 * Keyed by code, not by (lane, reason), and that is the whole reason this page
 * exists rather than a drawer. A code is deliberately REUSED across lanes wherever
 * the customer experience is identical — `unexpected_error` is issued by four —
 * so the question "what does this code mean" has an answer that spans several
 * emitters and cannot fit on the row that raised it. The dictionary list shows one
 * row per reason; this shows one page per thing a customer can actually quote.
 *
 * It also audits the code. Because the dictionary is a TRANSCRIPTION from each
 * owning repo (shared-types says so, and says it WILL drift), two lanes can end up
 * issuing the same code with different copy — `service_temporarily_unavailable`
 * does today. That is a real defect: a customer quoting one code is told two
 * different things depending on which service happened to fail. The page states it
 * rather than picking a winner, because silently rendering the first one is how the
 * drift survives.
 */
interface Issue {
  lane: ErrorLane;
  entry: ErrorReasonEntry;
}

export function ErrorCodeDetailPage() {
  const { code = '' } = useParams();
  const { role } = useMe();
  const canEdit = canEditErrorCodeDocs(role ?? 'new_member');
  const { overrides, docFor, refetchDocs } = useErrorCodeDocs();

  const issues: Issue[] = customerCodes().filter(({ entry }) => entry.code === code);

  if (issues.length === 0) {
    return (
      <div className="page">
        <div className="detail-back">
          <Link to="/error-codes" className="muted">
            ← Error dictionary
          </Link>
        </div>
        <p className="error">No code called “{code}” in the dictionary.</p>
        <p className="muted">
          A code in a log line but not here means the dictionary is out of date, not the
          line — the lanes live in other repos and this file is a transcription.
        </p>
      </div>
    );
  }

  const first = issues[0].entry;
  const messages = [...new Set(issues.map((i) => i.entry.customerMessage))];
  const inconsistent = messages.length > 1;
  const lanes = [...new Set(issues.map((i) => i.lane.key))];
  const docUrl = docFor(first);

  return (
    <div className="page err-dict">
      <div className="detail-back">
        <Link to="/error-codes" className="muted">
          ← Error dictionary
        </Link>
      </div>

      <DetailHead
        name={code}
        badge={<span className={`ec-lvl ec-lvl--${first.level}`}>{first.level}</span>}
        facts={[
          `${issues.length} reason${issues.length === 1 ? '' : 's'}`,
          `${lanes.length} lane${lanes.length === 1 ? '' : 's'}`,
          first.taxonomy.replace(/_/g, ' '),
        ]}
        curated
        scanLead="Transcribed from each lane's contract module"
        scanNote="Not scanned — edit shared-types/src/error-codes.ts to change it."
        actions={<CopyButton value={code} />}
      />

      <DetailBody
        rail={
          <>
            <RailCard title="Help-centre article">
              <ArticleField
                code={code}
                docUrl={docUrl}
                override={overrides.get(code)}
                canEdit={canEdit}
                onSaved={() => void refetchDocs()}
                variant="block"
              />
            </RailCard>
            <RailCard title="Emitted by">
              <div className="stack-sm">
                {issues.map(({ lane, entry }) => (
                  <div key={`${lane.key}:${entry.reason}`} className="ec-rail-lane">
                    <span className="err-reason">{lane.evt}</span>
                    <span className="cell-meta">{lane.repo}</span>
                  </div>
                ))}
              </div>
            </RailCard>
          </>
        }
      >
        <section>
          <h3 className="panel-title">What the customer is told</h3>
          {inconsistent ? (
            <>
              <p className="ec-drift">
                This code carries <strong>{messages.length} different messages</strong>. A
                customer quoting it is told different things depending on which service
                failed — the copy should be reconciled in the owning repos.
              </p>
              {issues.map(({ lane, entry }) => (
                <div key={`${lane.key}:${entry.reason}`} className="ec-drift-msg">
                  <div className="ec-drift-msg__lane">
                    <span className="badge">{lane.key}</span>
                    <span className="err-reason">{entry.reason}</span>
                  </div>
                  <p>{entry.customerMessage}</p>
                </div>
              ))}
            </>
          ) : (
            <p className="ec-lead">{first.customerMessage}</p>
          )}
        </section>

        <section>
          <h3 className="panel-title">
            Issued for {issues.length} reason{issues.length === 1 ? '' : 's'}
          </h3>
          <p className="detail-desc">
            <code>reason</code> is the internal routing key alert rules filter on. It is
            renameable; the code above is not.
          </p>
          <div className="ec-reasons">
            {issues.map(({ lane, entry }) => (
              <article key={`${lane.key}:${entry.reason}`} className="ec-reason">
                <header className="ec-reason__head">
                  <span className="err-reason">{entry.reason}</span>
                  <span className="badge">{lane.key}</span>
                  <span className={`ec-lvl ec-lvl--${entry.level}`}>{entry.level}</span>
                  <span className="cell-meta">{entry.taxonomy.replace(/_/g, ' ')}</span>
                </header>
                <p className="ec-reason__note">{entry.note}</p>
                <p className="ec-reason__src">
                  {lane.title} · <span className="err-reason">{lane.evt}</span> · {lane.module}
                </p>
              </article>
            ))}
          </div>
        </section>
      </DetailBody>
    </div>
  );
}
